import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { MonthlyDays, monthDays, defaultDay } from "@/components/MonthlyDays";
import { useAuth } from "@/context/AuthContext";
import { ExerciseImage } from "@/components/ExerciseImage";
import { toast } from "sonner";
import { nextBusinessDay, ymdLocal, formatBR } from "@/lib/holidays";
import {
  Plus, Loader2, X, Dumbbell, User, ArrowRightLeft, Move, Trash2, Copy, ClipboardPaste, Eye, AlertTriangle, FileDown, ChevronLeft, ChevronRight, Check, RefreshCw, CalendarClock, Image as ImageIcon,
} from "lucide-react";

const WEEKDAYS = [
  { i: 0, label: "Seg" }, { i: 1, label: "Ter" }, { i: 2, label: "Qua" },
  { i: 3, label: "Qui" }, { i: 4, label: "Sex" },
];
const MUSCLE_GROUPS = ["Peito", "Costas", "Pernas", "Ombro", "Bíceps", "Tríceps", "Antebraço", "Glúteo", "Panturrilha", "Core/Abdômen"];

const mondayStr = (offset) => {
  const n = new Date();
  const m = new Date(n.getFullYear(), n.getMonth(), n.getDate() - ((n.getDay() + 6) % 7) + offset * 7);
  return ymdLocal(m);
};

const imageSrc = (v) => (v ? (v.startsWith("http") ? v : `${process.env.REACT_APP_BACKEND_URL}/api/photo/${v}`) : null);

function WorkoutFields({ wType, setWType, modelId, setModelId, templates, exercises, libItems, setLibItems, manualText, setManualText }) {
  const [pickGroup, setPickGroup] = useState("todos");
  const [libPanelOpen, setLibPanelOpen] = useState(false);
  const filtered = pickGroup === "todos" ? exercises : exercises.filter((e) => e.muscle_group === pickGroup);
  const selectedIds = new Set(libItems.map((i) => i.exercise_id).filter(Boolean));

  const toggleCard = (ex) => {
    setLibItems((prev) => {
      if (prev.find((i) => i.exercise_id === ex.id)) return prev.filter((i) => i.exercise_id !== ex.id);
      return [...prev, { key: Math.random().toString(36).slice(2), exercise_id: ex.id, exercise_name: ex.name, muscle_group: ex.muscle_group, image_url: ex.image_url, sets: "", reps: "", load_note: "" }];
    });
  };
  const upd = (k, f, v) => setLibItems((p) => p.map((i) => (i.key === k ? { ...i, [f]: v } : i)));
  const rm = (k) => setLibItems((p) => p.filter((i) => i.key !== k));

  const TYPES = [
    { k: "model", label: "Modelo pronto", tid: "workout-type-model" },
    { k: "library", label: "Da biblioteca", tid: "workout-type-library" },
    { k: "manual", label: "À mão", tid: "workout-type-manual" },
  ];

  return (
    <div className="space-y-3 min-w-0">
      <Label className="text-slate-300">Montar treino</Label>
      <div className="flex gap-1.5 bg-[#0F1520] border border-[#263147] rounded-xl p-1">
        {TYPES.map((t) => (
          <button key={t.k} type="button" data-testid={t.tid} onClick={() => setWType(t.k)}
            className={`flex-1 h-9 rounded-lg text-xs font-medium transition-colors ${wType === t.k ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {wType === "model" && (
        <Select value={modelId} onValueChange={setModelId}>
          <SelectTrigger data-testid="workout-model-select" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue placeholder="Selecione um modelo" /></SelectTrigger>
          <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100 max-h-60">
            {templates.length === 0 ? <div className="px-3 py-2 text-sm text-slate-500">Nenhum modelo criado</div>
              : templates.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.items?.length || 0} ex.)</SelectItem>)}
          </SelectContent>
        </Select>
      )}

      {wType === "library" && (
        <div className="space-y-3">
          {!libPanelOpen && (
            <Button type="button" variant="outline" data-testid="open-lib-panel" onClick={() => setLibPanelOpen(true)}
              className="w-full h-11 border-dashed border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">
              <Plus className="w-4 h-4 mr-2" /> Adicionar exercícios da biblioteca
            </Button>
          )}

          {libPanelOpen && (
            <div className="border border-[#263147] rounded-2xl p-3 bg-[#0F1520]" data-testid="lib-panel">
              <div className="flex flex-wrap gap-1.5 mb-3">
                {["todos", ...MUSCLE_GROUPS].map((g) => (
                  <button key={g} type="button" data-testid={`lib-group-${g}`} onClick={() => setPickGroup(g)}
                    className={`h-8 px-3 rounded-full text-xs font-medium border transition-colors ${pickGroup === g ? "bg-emerald-500 text-[#0A0D12] border-emerald-500" : "bg-[#181F2C] text-slate-300 border-[#263147] hover:border-[#34435f]"}`}>
                    {g === "todos" ? "Todos" : g}
                  </button>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-72 overflow-y-auto overflow-x-hidden pr-1" data-testid="lib-cards">
                {filtered.length === 0 ? <p className="text-sm text-slate-500 col-span-full py-6 text-center">Nenhum exercício neste grupo.</p>
                : filtered.map((ex) => {
                  const sel = selectedIds.has(ex.id);
                  const img = imageSrc(ex.image_url);
                  return (
                    <button key={ex.id} type="button" data-testid={`lib-card-${ex.id}`} onClick={() => toggleCard(ex)}
                      className={`relative min-w-0 text-left rounded-xl border p-2 transition-all ${sel ? "border-emerald-500 bg-emerald-500/10" : "border-[#263147] bg-[#181F2C] hover:border-[#34435f]"}`}>
                      {sel && <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center z-10"><Check className="w-3 h-3 text-[#0A0D12]" /></span>}
                      <div className="w-full h-20 rounded-lg bg-[#0F1520] border border-[#263147] flex items-center justify-center mb-2 overflow-hidden">
                        {img ? <ExerciseImage value={ex.image_url} alt={ex.name} className="w-full h-full object-cover" /> : <ImageIcon className="w-6 h-6 text-slate-700" />}
                      </div>
                      <p className="text-xs font-medium text-slate-100 leading-tight line-clamp-2">{ex.name}</p>
                      <span className="text-[10px] text-slate-500">{ex.muscle_group}</span>
                    </button>
                  );
                })}
              </div>
              <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#263147]">
                <span className="text-xs text-slate-400">{selectedIds.size} selecionado(s)</span>
                <Button type="button" data-testid="lib-confirm" onClick={() => setLibPanelOpen(false)} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-4">Confirmar</Button>
              </div>
            </div>
          )}

          {libItems.length > 0 && (
            <div className="space-y-2">
              {libItems.map((it) => (
                <div key={it.key} className="bg-[#0F1520] border border-[#263147] rounded-xl p-2 flex flex-wrap items-center gap-2">
                  <span className="text-sm text-slate-200 flex-1 min-w-[8rem] truncate">{it.exercise_name}</span>
                  <Input value={it.sets} onChange={(e) => upd(it.key, "sets", e.target.value)} placeholder="Sér" className="h-8 w-14 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg text-xs" />
                  <Input value={it.reps} onChange={(e) => upd(it.key, "reps", e.target.value)} placeholder="Rep" className="h-8 w-14 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg text-xs" />
                  <Input value={it.load_note} onChange={(e) => upd(it.key, "load_note", e.target.value)} placeholder="Carga" className="h-8 w-20 bg-[#181F2C] border-[#263147] text-slate-100 rounded-lg text-xs" />
                  <Button type="button" variant="ghost" onClick={() => rm(it.key)} className="h-8 w-8 p-0 text-red-400 hover:bg-red-500/10 rounded-lg"><X className="w-3.5 h-3.5" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {wType === "manual" && (
        <Textarea data-testid="workout-manual-input" value={manualText} onChange={(e) => setManualText(e.target.value)}
          className="bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl min-h-[100px]" placeholder="Escreva o treino livremente..." />
      )}
    </div>
  );
}

export default function AgendaSemana({ refreshKey }) {
  const [times, setTimes] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [students, setStudents] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [exercises, setExercises] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clipboard, setClipboard] = useState(null);

  // assign dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [target, setTarget] = useState({ weekday: 0, time: "", encaixe: false });
  const [studentId, setStudentId] = useState("");
  const [weekdaysSel, setWeekdaysSel] = useState([]);
  const [bookMode, setBookMode] = useState("semanal");
  const [recurring, setRecurring] = useState(true);
  const [monthSel, setMonthSel] = useState("");
  const [monthDaysState, setMonthDaysState] = useState({});
  const [copyStatus, setCopyStatus] = useState(null);
  const [copying, setCopying] = useState(false);
  const [wType, setWType] = useState("model");
  const [modelId, setModelId] = useState("");
  const [libItems, setLibItems] = useState([]);
  const [manualText, setManualText] = useState("");
  const [saving, setSaving] = useState(false);

  // booking action dialog
  const [actionBooking, setActionBooking] = useState(null);
  const [actionMode, setActionMode] = useState("menu"); // menu | view | substitute | move
  const [subStudent, setSubStudent] = useState("");
  const [moveTarget, setMoveTarget] = useState({ weekday: 0, time: "" });
  const [pendingDelete, setPendingDelete] = useState(null);
  const [rescheduleDraft, setRescheduleDraft] = useState({ date: "", suggested: "" });
  const [downloadingWeek, setDownloadingWeek] = useState(false);
  const [weekOffset, setWeekOffset] = useState(0);

  const downloadWeekPdf = async () => {
    setDownloadingWeek(true);
    try {
      const res = await api.get("/agenda/week/pdf", { responseType: "blob", params: { week_start: mondayStr(weekOffset) } });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "agenda-semana.pdf";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF da semana gerado!");
    } catch (_) {
      toast.error("Falha ao gerar PDF");
    } finally {
      setDownloadingWeek(false);
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cfg, wk, st, tp, ex] = await Promise.all([
        api.get("/agenda/config"), api.get("/agenda/week", { params: { week_start: mondayStr(weekOffset) } }),
        api.get("/students", { params: { status: "ativo" } }),
        api.get("/templates"), api.get("/exercises"),
      ]);
      setTimes(cfg.data.times);
      setBookings(wk.data);
      setStudents(st.data);
      setTemplates(tp.data);
      setExercises(ex.data);
    } catch (_) {
      toast.error("Falha ao carregar agenda");
    } finally {
      setLoading(false);
    }
  }, [weekOffset]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const { user } = useAuth();
  const dismissKey = `copyMonthDismissed_${user?.user_id || user?.id || ""}`;
  const viewMonth = mondayStr(weekOffset).slice(0, 7);
  const loadCopyStatus = useCallback(async () => {
    try {
      const dismissed = JSON.parse(localStorage.getItem(dismissKey) || "[]");
      if (dismissed.includes(viewMonth)) { setCopyStatus(null); return; }
      const { data } = await api.get("/agenda/monthly-status", { params: { month: viewMonth } });
      setCopyStatus(data.students_to_copy > 0 ? data : null);
    } catch (_) { setCopyStatus(null); }
  }, [viewMonth, dismissKey]);
  useEffect(() => { loadCopyStatus(); }, [loadCopyStatus, refreshKey]);

  const dismissCopy = () => {
    const dismissed = JSON.parse(localStorage.getItem(dismissKey) || "[]");
    localStorage.setItem(dismissKey, JSON.stringify([...dismissed, viewMonth]));
    setCopyStatus(null);
  };
  const doCopyMonth = async () => {
    setCopying(true);
    try {
      const { data } = await api.post("/agenda/copy-month", { month: viewMonth });
      toast.success(`${data.created.length} aula(s) copiada(s) do mês anterior!`);
      if (data.skipped.length) toast.warning(`${data.skipped.length} dia(s) não copiado(s) (lotado ou duplicado).`);
      setCopyStatus(null); load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao copiar mês");
    } finally { setCopying(false); }
  };

  const cellKey = (wd, t) => `${wd}_${t}`;
  const grid = {};
  bookings.forEach((b) => {
    const k = cellKey(b.weekday, b.time);
    (grid[k] = grid[k] || []).push(b);
  });
  Object.values(grid).forEach((arr) => arr.sort((a, b) => a.slot_index - b.slot_index));

  // Entradas de reposição por data-alvo + horário original
  const ghosts = {};
  bookings.forEach((b) => {
    const r = b.reschedule;
    if (r && r.new_date) {
      const k = `${r.new_date}_${b.time}`;
      (ghosts[k] = ghosts[k] || []).push(b);
    }
  });

  const openAssign = (weekday, time, encaixe) => {
    setTarget({ weekday, time, encaixe });
    setStudentId(""); setWeekdaysSel([weekday]); setWType("model"); setModelId("");
    setLibItems([]); setManualText(""); setBookMode("semanal"); setRecurring(true);
    setMonthSel(mondayStr(weekOffset).slice(0, 7)); setMonthDaysState({});
    setAssignOpen(true);
  };

  const toggleWeekday = (i) => setWeekdaysSel((prev) => prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]);

  const buildWorkout = (st = { wType, modelId, libItems, manualText }) => {
    const { wType, modelId, libItems, manualText } = st;
    if (wType === "model") {
      const t = templates.find((x) => x.id === modelId);
      if (!t) return null;
      return { type: "model", model_name: t.name, manual_text: null, items: (t.items || []).map(({ exercise_name, sets, reps, load_note }) => ({ exercise_name, sets, reps, load_note })) };
    }
    if (wType === "library") return { type: "library", model_name: null, manual_text: null, items: libItems.map(({ key, ...r }) => r) };
    return { type: "manual", model_name: null, manual_text: manualText, items: [] };
  };

  const saveMonthly = async () => {
    if (!studentId) { toast.error("Selecione um aluno"); return; }
    const list = monthDays(monthSel, weekdaysSel).filter((d) => (monthDaysState[d.date] || defaultDay()).include);
    if (list.length === 0) { toast.error("Inclua ao menos um dia do mês"); return; }
    const missing = list.find((d) => { const st = monthDaysState[d.date] || defaultDay(); return st.wType === "model" && !st.modelId; });
    if (missing) { toast.error(`Escolha o modelo de treino de ${missing.label}`); return; }
    setSaving(true);
    try {
      const days = list.map((d) => ({ date: d.date, workout: buildWorkout(monthDaysState[d.date] || defaultDay()) }));
      const { data } = await api.post("/agenda/book-monthly", { student_id: studentId, time: target.time, days, encaixe: target.encaixe });
      if (data.created.length) toast.success(`Aluno agendado em ${data.created.length} dia(s) do mês!`);
      if (data.skipped.length) toast.warning(`${data.skipped.length} dia(s) não agendado(s) (lotado ou duplicado).`);
      setAssignOpen(false);
      load(); loadCopyStatus();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao agendar");
    } finally {
      setSaving(false);
    }
  };

  const saveAssign = async () => {
    if (bookMode === "mensal") { saveMonthly(); return; }
    if (!studentId) { toast.error("Selecione um aluno"); return; }
    if (weekdaysSel.length === 0) { toast.error("Marque ao menos um dia"); return; }
    const workout = buildWorkout();
    if (wType === "model" && !modelId) { toast.error("Selecione um modelo"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/agenda/book", { student_id: studentId, time: target.time, weekdays: weekdaysSel, workout, encaixe: target.encaixe, recurring, week_start: mondayStr(weekOffset) });
      if (data.created.length) toast.success(`Aluno agendado em ${data.created.length} dia(s)!`);
      if (data.skipped.length) {
        const full = data.skipped.filter((s) => s.reason === "lotado").length;
        const dup = data.skipped.filter((s) => s.reason === "duplicado").length;
        if (full) toast.warning(`${full} dia(s) lotado(s) — use o botão vermelho para encaixe.`);
        if (dup) toast.info(`${dup} dia(s) já tinham este aluno.`);
      }
      if (!data.created.length && !data.skipped.length) toast.error("Nada foi agendado");
      setAssignOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao agendar");
    } finally {
      setSaving(false);
    }
  };

  const openBooking = (b) => { setActionBooking(b); setActionMode("menu"); setSubStudent(""); setMoveTarget({ weekday: b.weekday, time: b.time }); };

  const doSubstitute = async () => {
    if (!subStudent) { toast.error("Selecione o novo aluno"); return; }
    try {
      await api.put(`/agenda/${actionBooking.id}`, { student_id: subStudent });
      toast.success("Aluno substituído!");
      setActionBooking(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao substituir"); }
  };

  const doMove = async () => {
    try {
      await api.patch(`/agenda/${actionBooking.id}/move`, moveTarget);
      toast.success("Aluno movido!");
      setActionBooking(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao mover"); }
  };

  const doDelete = () => { setPendingDelete(actionBooking); setActionBooking(null); };
  const confirmDelete = async () => {
    try {
      await api.delete(`/agenda/${pendingDelete.id}`);
      toast.success("Removido!");
      setPendingDelete(null); load();
    } catch (_) { toast.error("Falha ao remover"); }
  };

  const doCopy = () => { setClipboard(actionBooking.workout); toast.success("Treino copiado!"); setActionBooking(null); };
  const doPaste = async () => {
    try {
      await api.put(`/agenda/${actionBooking.id}`, { workout: clipboard });
      toast.success("Treino colado!");
      setActionBooking(null); load();
    } catch (_) { toast.error("Falha ao colar"); }
  };

  const openReschedule = () => {
    const sug = nextBusinessDay(weekDates[actionBooking.weekday]);
    setRescheduleDraft({ date: ymdLocal(sug), suggested: formatBR(sug) });
    setActionMode("reschedule");
  };
  const doReschedule = async () => {
    if (!rescheduleDraft.date) { toast.error("Escolha a nova data"); return; }
    try {
      await api.patch(`/agenda/${actionBooking.id}/reschedule`, { new_date: rescheduleDraft.date, original_date: ymdLocal(weekDates[actionBooking.weekday]) });
      toast.success("Reposição criada — dia original marcado como Faltou.");
      setActionBooking(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao reagendar"); }
  };
  const resolveReschedule = async (b, status) => {
    try {
      await api.patch(`/agenda/${b.id}/makeup`, { status, date: b.reschedule.new_date });
      toast.success(status === "presente" ? "Reposição: presença registrada!" : "Reposição: falta registrada.");
      load();
    } catch (_) { toast.error("Falha ao registrar reposição"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-7 h-7 text-emerald-500 animate-spin" /></div>;

  const now = new Date();
  const monday = new Date(now); monday.setHours(0, 0, 0, 0);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7) + weekOffset * 7);
  const weekDates = WEEKDAYS.map((_, i) => { const dt = new Date(monday); dt.setDate(monday.getDate() + i); return dt; });
  const todayCol = weekOffset === 0 ? (now.getDay() + 6) % 7 : -1;
  const rawMonthYear = monday.toLocaleDateString("pt-BR", { month: "long", year: "numeric" });
  const monthYear = rawMonthYear.charAt(0).toUpperCase() + rawMonthYear.slice(1);
  const sameMonth = weekDates[0].getMonth() === weekDates[4].getMonth();
  const rangeLabel = sameMonth
    ? `${weekDates[0].getDate()} a ${weekDates[4].getDate()} de ${monthYear}`
    : `${weekDates[0].toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} a ${weekDates[4].toLocaleDateString("pt-BR", { day: "2-digit", month: "short" })} de ${weekDates[4].getFullYear()}`;
  const weekLabel = weekOffset === 0 ? "Esta semana" : weekOffset === 1 ? "Próxima semana" : weekOffset === -1 ? "Semana passada" : weekOffset > 0 ? `Em ${weekOffset} semanas` : `${Math.abs(weekOffset)} semanas atrás`;

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1">
            <Button variant="outline" size="icon" data-testid="week-prev" onClick={() => setWeekOffset((o) => o - 1)} className="h-10 w-10 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl"><ChevronLeft className="w-4 h-4" /></Button>
            <Button variant="outline" size="icon" data-testid="week-next" onClick={() => setWeekOffset((o) => o + 1)} className="h-10 w-10 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl"><ChevronRight className="w-4 h-4" /></Button>
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-lg font-bold font-heading text-slate-100 capitalize" data-testid="agenda-month-year">{monthYear}</h2>
              <span className={`text-[11px] px-2 py-0.5 rounded-full ${weekOffset === 0 ? "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30" : "bg-slate-500/15 text-slate-400 border border-slate-600/40"}`} data-testid="agenda-week-label">{weekLabel}</span>
              {weekOffset !== 0 && <button data-testid="week-today" onClick={() => setWeekOffset(0)} className="text-xs text-emerald-400 hover:text-emerald-300 underline">Hoje</button>}
            </div>
            <p className="text-sm text-slate-400" data-testid="agenda-week-range">Semana de {rangeLabel}</p>
          </div>
        </div>
        <Button data-testid="week-download-pdf" onClick={downloadWeekPdf} disabled={downloadingWeek}
          className="h-11 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl px-5 w-fit">
          {downloadingWeek ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <FileDown className="w-4 h-4 mr-2" />} PDF da Semana
        </Button>
      </div>
      {copyStatus && (
        <div data-testid="copy-month-banner" className="mb-4 flex flex-col sm:flex-row sm:items-center gap-3 bg-[#181F2C] border border-emerald-500/30 rounded-2xl px-4 py-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-slate-100">Copiar aulas do mês anterior?</p>
            <p className="text-xs text-slate-400 truncate" title={copyStatus.names.join(", ")}>{copyStatus.names.length} aluno(s) sem aulas mensais neste mês: {copyStatus.names.join(", ")}</p>
          </div>
          <div className="flex gap-2">
            <Button data-testid="copy-month-yes" onClick={doCopyMonth} disabled={copying} className="h-9 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-lg px-5">
              {copying ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sim"}
            </Button>
            <Button data-testid="copy-month-no" variant="outline" onClick={dismissCopy} className="h-9 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-lg px-5">Não</Button>
          </div>
        </div>
      )}
      {clipboard && (
        <div className="mb-4 flex items-center gap-2 text-sm text-emerald-400 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-4 py-2 w-fit">
          <Copy className="w-4 h-4" /> Treino copiado — abra um aluno e use "Colar treino"
          <button onClick={() => setClipboard(null)} className="ml-2 text-slate-400 hover:text-slate-200"><X className="w-3.5 h-3.5" /></button>
        </div>
      )}

      <div className="overflow-x-auto rounded-2xl border border-[#263147]" data-testid="agenda-week-grid">
        <table className="w-full border-collapse min-w-[720px]">
          <thead>
            <tr className="bg-[#181F2C]">
              <th className="sticky left-0 bg-[#181F2C] z-10 text-left text-xs font-semibold text-slate-400 uppercase tracking-wider px-3 py-3 w-20">Horário</th>
              {WEEKDAYS.map((d, i) => {
                const isToday = i === todayCol;
                return (
                  <th key={d.i} className={`px-2 py-2.5 text-center ${isToday ? "bg-emerald-500/10" : ""}`}>
                    <div className={`text-xs font-semibold uppercase tracking-wider ${isToday ? "text-emerald-400" : "text-slate-300"}`}>{d.label}</div>
                    <div className={`text-base font-bold font-heading leading-tight ${isToday ? "text-emerald-400" : "text-slate-500"}`}>{String(weekDates[i].getDate()).padStart(2, "0")}</div>
                    {isToday && <div className="text-[9px] text-emerald-400 uppercase tracking-wide">hoje</div>}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {times.map((t) => (
              <tr key={t} className="border-t border-[#263147]">
                <td className="sticky left-0 bg-[#0A0D12] z-10 text-sm font-semibold text-emerald-400 px-3 py-2 align-top">{t}</td>
                {WEEKDAYS.map((d) => {
                  const cell = grid[cellKey(d.i, t)] || [];
                  const normal = cell.filter((b) => !b.is_encaixe);
                  const hasEncaixe = cell.some((b) => b.is_encaixe);
                  return (
                    <td key={d.i} className={`px-1.5 py-2 align-top min-w-[130px] ${d.i === todayCol ? "bg-emerald-500/5" : ""}`}>
                      <div className="space-y-1.5">
                        {cell.map((b) => (
                          <button key={b.id} data-testid={`agenda-chip-${b.id}`} onClick={() => openBooking(b)}
                            className={`w-full text-left text-xs rounded-lg px-2 py-1.5 truncate transition-colors ${
                              b.is_encaixe ? "bg-red-500/15 text-red-300 border border-red-500/40 hover:bg-red-500/25"
                                : "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25"
                            }`} title={b.student_name}>
                            {b.is_encaixe && <span className="font-bold">5º </span>}
                            <span>{b.student_nickname || b.student_name}</span>
                            {b.week_start && (b.monthly
                              ? <span data-testid={`agenda-chip-monthly-${b.id}`} title="Agendamento mensal" className="ml-1 text-[9px] font-semibold uppercase px-1 rounded bg-violet-500/20 text-violet-300">Mês</span>
                              : <span data-testid={`agenda-chip-oneoff-${b.id}`} title="Só esta semana" className="ml-1 text-[9px] font-semibold uppercase px-1 rounded bg-sky-500/20 text-sky-300">1x</span>)}
                            {b.reschedule?.original_date && b.reschedule.original_date === ymdLocal(weekDates[b.weekday]) && (
                              <span data-testid={`agenda-chip-faltou-${b.id}`} className="ml-1 text-[9px] font-semibold uppercase px-1 rounded bg-red-500/20 text-red-300">Faltou</span>
                            )}
                          </button>
                        ))}
                        {(ghosts[`${ymdLocal(weekDates[d.i])}_${t}`] || []).map((b) => {
                          const st = b.reschedule.status;
                          const pending = st !== "presente" && st !== "faltou";
                          return (
                          <div key={`ghost-${b.id}`} data-testid={`agenda-ghost-${b.id}`}
                            className="w-full text-xs rounded-lg px-2 py-1.5 border border-dashed bg-[#181F2C]" style={{ borderColor: pending ? "#FFD700" : "#263147" }}>
                            <div className="flex items-center gap-1 truncate">
                              <RefreshCw className="w-3 h-3 shrink-0" style={{ color: pending ? "#FFD700" : "#64748b" }} />
                              <span data-testid={`agenda-ghost-name-${b.id}`} className={`truncate ${pending ? "" : "text-slate-300"}`} style={pending ? { color: "#FFD700" } : undefined} title={b.student_name}>{b.student_nickname || b.student_name}</span>
                            </div>
                            {pending ? (
                            <div className="flex gap-1 mt-1">
                              <button data-testid={`reschedule-confirm-${b.id}`} onClick={() => resolveReschedule(b, "presente")} title="Presente"
                                className="flex-1 h-6 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/40 hover:bg-emerald-500/30 flex items-center justify-center"><Check className="w-3 h-3" /></button>
                              <button data-testid={`reschedule-cancel-${b.id}`} onClick={() => resolveReschedule(b, "faltou")} title="Faltou"
                                className="flex-1 h-6 rounded bg-red-500/20 text-red-300 border border-red-500/40 hover:bg-red-500/30 flex items-center justify-center"><X className="w-3 h-3" /></button>
                            </div>
                            ) : (
                              <div data-testid={`agenda-ghost-status-${b.id}`} className={`mt-1 text-[10px] font-semibold uppercase ${st === "presente" ? "text-emerald-400" : "text-red-400"}`}>Reposição · {st === "presente" ? "Presente" : "Faltou"}</div>
                            )}
                          </div>
                          );
                        })}
                        {normal.length < 4 && (
                          <button data-testid={`agenda-add-${d.i}-${t}`} onClick={() => openAssign(d.i, t, false)}
                            className="w-full text-xs rounded-lg px-2 py-1.5 border border-dashed border-[#263147] text-slate-500 hover:border-emerald-500/50 hover:text-emerald-400 transition-colors flex items-center justify-center gap-1">
                            <Plus className="w-3 h-3" /> vaga
                          </button>
                        )}
                        {normal.length === 4 && !hasEncaixe && (
                          <button data-testid={`agenda-encaixe-${d.i}-${t}`} onClick={() => openAssign(d.i, t, true)}
                            className="w-full text-xs rounded-lg px-2 py-1.5 bg-red-500 text-white font-semibold hover:bg-red-600 transition-colors flex items-center justify-center gap-1">
                            <Plus className="w-3 h-3" /> 5º encaixe
                          </button>
                        )}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Assign dialog */}
      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-modal" className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl w-[95vw] max-w-lg max-h-[90vh] overflow-y-auto overflow-x-hidden p-4 sm:p-6 [&>*]:min-w-0">
          <DialogHeader>
            <DialogTitle className="font-heading flex flex-wrap items-center gap-2 pr-6">
              <User className="w-5 h-5 text-emerald-400" /> Agendar aluno — {target.time}
              {target.encaixe && <Badge className="bg-red-500/15 text-red-400 border-red-500/30">Encaixe</Badge>}
            </DialogTitle>
            <DialogDescription className="text-slate-400">Escolha o aluno, os dias e monte o treino.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-1">
            <div className="flex gap-1.5 bg-[#0F1520] border border-[#263147] rounded-xl p-1" data-testid="assign-mode-selector">
              {[{ k: "semanal", label: "Semanal" }, { k: "mensal", label: "Mensal" }].map((m) => (
                <button key={m.k} type="button" data-testid={`assign-mode-${m.k}`} onClick={() => setBookMode(m.k)}
                  className={`flex-1 h-9 rounded-lg text-sm font-medium transition-colors ${bookMode === m.k ? "bg-emerald-500 text-[#0A0D12]" : "text-slate-400 hover:text-slate-200"}`}>
                  {m.label}
                </button>
              ))}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Aluno *</Label>
              <Select value={studentId} onValueChange={setStudentId}>
                <SelectTrigger data-testid="assign-student-select" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue placeholder="Selecione o aluno" /></SelectTrigger>
                <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100 max-h-60">
                  {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ("{s.nickname}")</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Dias da semana *</Label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => (
                  <button key={d.i} type="button" data-testid={`assign-weekday-${d.i}`} onClick={() => toggleWeekday(d.i)}
                    className={`flex-1 basis-[3.5rem] min-w-[3.5rem] h-11 rounded-xl text-sm font-medium border transition-colors ${
                      weekdaysSel.includes(d.i) ? "bg-emerald-500 text-[#0A0D12] border-emerald-500" : "bg-[#0F1520] text-slate-400 border-[#263147] hover:border-[#34435f]"
                    }`}>{d.label}</button>
                ))}
              </div>
              {bookMode === "semanal" && <label htmlFor="assign-recurring" className="flex items-center justify-between gap-3 mt-1 px-3 py-2.5 rounded-xl border border-[#263147] bg-[#0F1520] cursor-pointer">
                <span className="text-sm text-slate-200">Repetir toda semana (recorrente)
                  <span className="block text-[11px] text-slate-500">{recurring ? "Repete automaticamente nos dias escolhidos" : "Vale apenas para esta semana"}</span>
                </span>
                <Switch id="assign-recurring" data-testid="assign-recurring-toggle" checked={recurring} onCheckedChange={setRecurring} className="data-[state=checked]:bg-emerald-500 data-[state=unchecked]:bg-[#263147]" />
              </label>}
            </div>
            {bookMode === "mensal" ? (
              <MonthlyDays month={monthSel} setMonth={setMonthSel} weekdays={weekdaysSel} days={monthDaysState} setDays={setMonthDaysState}
                templates={templates} exercises={exercises} WorkoutFields={WorkoutFields} />
            ) : (
              <WorkoutFields {...{ wType, setWType, modelId, setModelId, templates, exercises, libItems, setLibItems, manualText, setManualText }} />
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</Button>
            <Button data-testid="assign-save-button" onClick={saveAssign} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Agendar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking action dialog */}
      <Dialog open={!!actionBooking} onOpenChange={(o) => !o && setActionBooking(null)}>
        <DialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl max-w-md">
          {actionBooking && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-2">
                  <Dumbbell className="w-5 h-5 text-emerald-400" /> {actionBooking.student_name}
                </DialogTitle>
                <DialogDescription className="text-slate-400">
                  {WEEKDAYS[actionBooking.weekday]?.label} • {actionBooking.time}{actionBooking.is_encaixe ? " • Encaixe" : ""}
                </DialogDescription>
              </DialogHeader>

              {actionMode === "menu" && (
                <div className="grid grid-cols-2 gap-2 py-2">
                  <Button variant="outline" data-testid="booking-view" onClick={() => setActionMode("view")} className="h-12 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl justify-start"><Eye className="w-4 h-4 mr-2" /> Ver treino</Button>
                  <Button variant="outline" data-testid="booking-substitute" onClick={() => setActionMode("substitute")} className="h-12 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl justify-start"><ArrowRightLeft className="w-4 h-4 mr-2" /> Substituir</Button>
                  <Button variant="outline" data-testid="booking-move" onClick={() => setActionMode("move")} className="h-12 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl justify-start"><Move className="w-4 h-4 mr-2" /> Mover</Button>
                  <Button variant="outline" data-testid="booking-copy" onClick={doCopy} className="h-12 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl justify-start"><Copy className="w-4 h-4 mr-2" /> Copiar treino</Button>
                  <Button variant="outline" data-testid="booking-reschedule" onClick={openReschedule} className="h-12 border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl justify-start"><RefreshCw className="w-4 h-4 mr-2" style={{ color: "#FFD700" }} /> Reagendar</Button>
                  {clipboard && <Button variant="outline" data-testid="booking-paste" onClick={doPaste} className="h-12 border-emerald-500/40 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20 rounded-xl justify-start"><ClipboardPaste className="w-4 h-4 mr-2" /> Colar treino</Button>}
                  <Button variant="outline" data-testid="booking-delete" onClick={doDelete} className="h-12 border-[#263147] bg-[#0F1520] text-red-400 hover:bg-red-500/10 rounded-xl justify-start col-span-2"><Trash2 className="w-4 h-4 mr-2" /> Excluir</Button>
                </div>
              )}

              {actionMode === "view" && (
                <div className="py-2 max-h-[50vh] overflow-y-auto">
                  {actionBooking.workout?.model_name && <p className="text-sm text-emerald-400 mb-2">Modelo: {actionBooking.workout.model_name}</p>}
                  {actionBooking.workout?.type === "manual" ? (
                    <p className="text-sm text-slate-300 whitespace-pre-wrap">{actionBooking.workout.manual_text || "—"}</p>
                  ) : actionBooking.workout?.items?.length ? (
                    <div className="space-y-1.5">
                      {actionBooking.workout.items.map((it, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm bg-[#0F1520] border border-[#263147] rounded-lg px-3 py-2">
                          <span className="flex-1 text-slate-200">{it.exercise_name}</span>
                          <span className="text-xs text-slate-400">{[it.sets && `${it.sets}x`, it.reps, it.load_note].filter(Boolean).join(" · ")}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="text-sm text-slate-500">Sem treino definido.</p>}
                  <Button variant="outline" onClick={() => setActionMode("menu")} className="mt-3 border-[#263147] bg-[#0F1520] text-slate-200 rounded-xl">Voltar</Button>
                </div>
              )}

              {actionMode === "substitute" && (
                <div className="py-2 space-y-3">
                  <div className="flex items-start gap-2 text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2">
                    <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> Já tem alguém aqui. Substituir?
                  </div>
                  <Select value={subStudent} onValueChange={setSubStudent}>
                    <SelectTrigger data-testid="substitute-student-select" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl"><SelectValue placeholder="Novo aluno" /></SelectTrigger>
                    <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100 max-h-60">
                      {students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name} ("{s.nickname}")</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setActionMode("menu")} className="border-[#263147] bg-[#0F1520] text-slate-200 rounded-xl">Voltar</Button>
                    <Button data-testid="substitute-confirm" onClick={doSubstitute} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">Confirmar substituição</Button>
                  </div>
                </div>
              )}

              {actionMode === "move" && (
                <div className="py-2 space-y-3">
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-slate-300 text-xs">Dia</Label>
                      <Select value={String(moveTarget.weekday)} onValueChange={(v) => setMoveTarget((m) => ({ ...m, weekday: Number(v) }))}>
                        <SelectTrigger data-testid="move-weekday-select" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100">
                          {WEEKDAYS.map((d) => <SelectItem key={d.i} value={String(d.i)}>{d.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-slate-300 text-xs">Horário</Label>
                      <Select value={moveTarget.time} onValueChange={(v) => setMoveTarget((m) => ({ ...m, time: v }))}>
                        <SelectTrigger data-testid="move-time-select" className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1"><SelectValue placeholder="Horário" /></SelectTrigger>
                        <SelectContent className="bg-[#181F2C] border-[#263147] text-slate-100 max-h-60">
                          {times.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setActionMode("menu")} className="border-[#263147] bg-[#0F1520] text-slate-200 rounded-xl">Voltar</Button>
                    <Button data-testid="move-confirm" onClick={doMove} className="flex-1 bg-emerald-500 hover:bg-emerald-600 text-[#0A0D12] font-semibold rounded-xl">Mover aluno</Button>
                  </div>
                </div>
              )}
              {actionMode === "reschedule" && (
                <div className="py-2 space-y-3">
                  <div className="flex items-center gap-2 bg-amber-500/10 border border-amber-500/30 rounded-xl px-3 py-2 text-sm" style={{ color: "#FFD700" }}>
                    <CalendarClock className="w-4 h-4 shrink-0" />
                    <span>Data sugerida: <span className="font-semibold">{rescheduleDraft.suggested}</span> — próximo dia útil</span>
                  </div>
                  <div>
                    <Label className="text-slate-300 text-xs">Nova data</Label>
                    <Input type="date" data-testid="reschedule-date-input" value={rescheduleDraft.date}
                      onChange={(e) => { const v = e.target.value; setRescheduleDraft({ date: v, suggested: v ? formatBR(new Date(v + "T00:00:00")) : "" }); }}
                      className="h-11 bg-[#0F1520] border-[#263147] text-slate-100 rounded-xl mt-1" />
                  </div>
                  <p className="text-xs text-slate-500">O aluno ficará em dourado no dia original e no novo dia (fantasma), até você confirmar (✓) ou cancelar (✗) no novo dia.</p>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setActionMode("menu")} className="border-[#263147] bg-[#0F1520] text-slate-200 rounded-xl">Voltar</Button>
                    <Button data-testid="reschedule-confirm" onClick={doReschedule} className="flex-1 text-[#0A0D12] font-semibold rounded-xl hover:opacity-90" style={{ backgroundColor: "#FFD700" }}>Reagendar</Button>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
        <AlertDialogContent className="bg-[#181F2C] border-[#263147] text-slate-100 rounded-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remover aluno do horário?</AlertDialogTitle>
            <AlertDialogDescription className="text-slate-400">
              {pendingDelete && `${pendingDelete.student_name} será removido de ${WEEKDAYS[pendingDelete.weekday]?.label} às ${pendingDelete.time}. Esta ação não pode ser desfeita.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-[#263147] bg-[#0F1520] text-slate-200 hover:bg-[#1E2738] rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-delete-booking" onClick={confirmDelete} className="bg-red-500 hover:bg-red-600 text-white rounded-xl">Remover</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
